"use client";
import { Logo } from "../Logo";
import { Button } from "../button";
import { NavBarItem } from "./navbar-item";
import {
  useMotionValueEvent,
  useScroll,
  motion,
  AnimatePresence,
} from "framer-motion";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Link } from "next-view-transitions";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { FeaturesMenu } from "./features-menu";

type Props = {
  navItems: {
    link: string;
    title: string;
    target?: "_blank";
  }[];
};

export const DesktopNavbar = ({ navItems }: Props) => {
  const { scrollY } = useScroll();
  const [showBackground, setShowBackground] = useState(false);
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations("nav");

  useMotionValueEvent(scrollY, "change", (value) => {
    setShowBackground(value > 100);
  });

  const switchLocale = (newLocale: string) => {
    const newPath = pathname.replace(`/${locale}`, `/${newLocale}`);
    router.push(newPath);
  };

  return (
    <div
      className={cn(
        "w-full flex relative justify-between px-4 py-2 rounded-full bg-transparent transition duration-200",
        showBackground &&
          "bg-neutral-50 dark:bg-neutral-900 shadow-[0px_-2px_0px_0px_var(--neutral-100),0px_2px_0px_0px_var(--neutral-100)] dark:shadow-[0px_-2px_0px_0px_var(--neutral-800),0px_2px_0px_0px_var(--neutral-800)]"
      )}
    >
      <AnimatePresence>
        {showBackground && (
          <motion.div
            key={String(showBackground)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 h-full w-full bg-neutral-100 dark:bg-neutral-800 pointer-events-none [mask-image:linear-gradient(to_bottom,white,transparent,white)] rounded-full"
          />
        )}
      </AnimatePresence>
      <div className="flex flex-row gap-2 items-center relative z-10">
        <Logo />
        <div className="flex items-center gap-1.5">
          <FeaturesMenu />
          {navItems.map((item) => (
            <NavBarItem href={item.link} key={item.title} target={item.target}>
              {item.title}
            </NavBarItem>
          ))}
        </div>
      </div>
      <div className="flex space-x-2 items-center relative z-10">
        {/* Locale switcher */}
        <div className="flex items-center gap-1 rounded-full border border-neutral-200 dark:border-neutral-700 px-1 py-0.5">
          <button
            onClick={() => switchLocale("en")}
            className={cn(
              "text-xs px-2 py-1 rounded-full transition-colors",
              locale === "en"
                ? "bg-neutral-900 dark:bg-white text-white dark:text-black font-medium"
                : "text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
            )}
          >
            EN
          </button>
          <button
            onClick={() => switchLocale("de")}
            className={cn(
              "text-xs px-2 py-1 rounded-full transition-colors",
              locale === "de"
                ? "bg-neutral-900 dark:bg-white text-white dark:text-black font-medium"
                : "text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
            )}
          >
            DE
          </button>
        </div>
        <Button variant="simple" as={Link} href="/login">
          {t("login")}
        </Button>
        <Button as={Link} href="/signup">
          {t("signup")}
        </Button>
      </div>
    </div>
  );
};
